import {
  DEFAULT_MATCH_CONFIG,
  DEFAULT_ROUND_CONFIG,
  EMPTY_MATCH_STATE,
  defaultRandomSource,
  generateTargetMs,
  recordRoundResult,
  type Attempt,
  type MatchState,
} from '@ranked-waiting/game-core';
import { renderPageShell } from '../lib/page-shell';
import { renderRoundStage } from '../lib/round-stage';
import { formatDurationMs } from '../lib/format';
import { resultBadgeMarkup } from '../lib/result-badge';
import { accuracyBarMarkup } from '../lib/accuracy-bar';
import type { RouteHandler } from '../lib/router';

function renderScoreboard(match: MatchState): string {
  return `
    <div class="scoreboard" aria-label="Match score">
      <span class="scoreboard__slot">P1 <strong>${match.scores.a}</strong></span>
      <span class="scoreboard__divider">ROUND ${match.history.length + (match.phase === 'complete' ? 0 : 1)} / ${DEFAULT_MATCH_CONFIG.maxRounds}</span>
      <span class="scoreboard__slot">P2 <strong>${match.scores.b}</strong></span>
    </div>
  `;
}

export const renderLocalDuel: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'LOCAL DUEL',
    subtitle: 'Best of five. Pass the device between rounds; closest to the target wins the round.',
    body: `<div id="scoreboard-mount"></div><div class="round-mount" id="round-mount"></div>`,
  });

  const scoreboardMount = body.querySelector<HTMLDivElement>('#scoreboard-mount')!;
  const mount = body.querySelector<HTMLDivElement>('#round-mount')!;
  let match: MatchState = EMPTY_MATCH_STATE;
  let destroyRound: (() => void) | void;

  function updateScoreboard() {
    scoreboardMount.innerHTML = renderScoreboard(match);
  }

  function renderHandoff(nextLabel: string, onContinue: () => void) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Pass the device</p>
        <h2>${nextLabel}</h2>
        <button class="button button--primary" type="button" data-action="continue">I'm ready</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="continue"]')?.addEventListener('click', onContinue);
  }

  function renderRoundResult(attemptA: Attempt, attemptB: Attempt, onContinue: () => void) {
    const winnerLabel =
      match.history[match.history.length - 1]?.winner === 'tie'
        ? 'Tie, no round point'
        : match.history[match.history.length - 1]?.winner === 'a'
          ? 'Player 1 takes the round'
          : 'Player 2 takes the round';

    mount.innerHTML = `
      <div class="round-result-pair">
        <p class="handoff__label">${winnerLabel}</p>
        <div class="round-result-pair__grid">
          <div class="round-result${attemptA.isFalseStart ? ' round-result--false-start' : ''}">
            <div><span>Player 1</span><strong>${attemptA.isFalseStart ? 'FALSE START' : `${formatDurationMs(attemptA.elapsedMs)}s`}</strong></div>
            <div><span>Result</span>${resultBadgeMarkup(attemptA)}</div>
            ${accuracyBarMarkup(attemptA)}
          </div>
          <div class="round-result${attemptB.isFalseStart ? ' round-result--false-start' : ''}">
            <div><span>Player 2</span><strong>${attemptB.isFalseStart ? 'FALSE START' : `${formatDurationMs(attemptB.elapsedMs)}s`}</strong></div>
            <div><span>Result</span>${resultBadgeMarkup(attemptB)}</div>
            ${accuracyBarMarkup(attemptB)}
          </div>
        </div>
        <p class="handoff__label">Target was ${formatDurationMs(attemptA.targetMs)}s</p>
        <button class="button button--primary" type="button" data-action="continue">Continue</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="continue"]')?.addEventListener('click', onContinue);
  }

  function renderMatchComplete() {
    const winnerLabel = match.winner === 'a' ? 'Player 1 wins the match!' : match.winner === 'b' ? 'Player 2 wins the match!' : 'Match ended level.';
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Match complete</p>
        <h2>${winnerLabel}</h2>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="rematch">Rematch</button>
          <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="rematch"]')?.addEventListener('click', () => {
      match = EMPTY_MATCH_STATE;
      updateScoreboard();
      playRound();
    });
    mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => navigate('play'));
  }

  function playRound() {
    updateScoreboard();
    const targetMs = generateTargetMs(defaultRandomSource, DEFAULT_ROUND_CONFIG);

    renderHandoff('Player 1, get ready', () => {
      destroyRound = renderRoundStage(mount, {
        config: DEFAULT_ROUND_CONFIG,
        fixedTargetMs: targetMs,
        label: 'Player 1',
        // onStop, not onComplete: player 1 hands the device off immediately
        // on release rather than seeing their own solo reveal first — the
        // one combined comparison screen after player 2 (below) is the only
        // reveal step, not a private one per player then a repeat.
        onStop: (attemptA) => {
          if (destroyRound) destroyRound();
          renderHandoff('Player 2, get ready', () => {
            destroyRound = renderRoundStage(mount, {
              config: DEFAULT_ROUND_CONFIG,
              fixedTargetMs: targetMs,
              label: 'Player 2',
              onStop: (attemptB) => {
                if (destroyRound) destroyRound();
                const result = recordRoundResult(match, attemptA, attemptB, DEFAULT_MATCH_CONFIG);
                if (result.ok) match = result.state;
                updateScoreboard();
                renderRoundResult(attemptA, attemptB, () => {
                  if (match.phase === 'complete') renderMatchComplete();
                  else playRound();
                });
              },
            });
          });
        },
      });
    });
  }

  playRound();

  return () => {
    if (destroyRound) destroyRound();
  };
};
